import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Send, 
  Bot, 
  User, 
  Lightbulb, 
  Edit3, 
  MessageCircle,
  Copy,
  Check,
  AlertCircle
} from 'lucide-react';
import aiService from '../services/aiService';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AssistantContainer = styled(motion.div)`
  width: 90vw;
  max-width: 800px;
  height: 80vh;
  background: ${props => props.theme.colors.surface};
  border-radius: 16px;
  border: 1px solid ${props => props.theme.colors.border};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const AssistantHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ModeIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.theme.colors.primary}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.primary};
`;

const ModeInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const ModeTitle = styled.h3`
  color: ${props => props.theme.colors.text};
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const ModeDescription = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 14px;
  margin: 0;
`;

const CloseButton = styled(motion.button)`
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.hover};
    color: ${props => props.theme.colors.text};
  }
`;

const ChatArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Message = styled(motion.div)`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const MessageAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.isUser ? props.theme.colors.primary : props.theme.colors.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  flex-shrink: 0;
`;

const MessageContent = styled.div`
  flex: 1;
  background: ${props => props.isUser ? props.theme.colors.primary : props.theme.colors.hover};
  color: ${props => props.isUser ? '#ffffff' : props.theme.colors.text};
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.5;
  position: relative;
  white-space: pre-wrap;
  word-wrap: break-word;
  
  &::after {
    content: '';
    display: ${props => props.isStreaming ? 'inline-block' : 'none'};
    width: 2px;
    height: 16px;
    background: ${props => props.isUser ? '#ffffff' : props.theme.colors.primary};
    margin-left: 2px;
    animation: blink 1s infinite;
  }
  
  @keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }
`;

const MessageActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const ActionButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border: none;
  border-radius: 6px;
  background: ${props => props.isUser ? 'rgba(255,255,255,0.2)' : props.theme.colors.primary};
  color: ${props => props.isUser ? '#ffffff' : '#ffffff'};
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.isUser ? 'rgba(255,255,255,0.3)' : props.theme.colors.primary}dd;
  }
`;

const InputArea = styled.div`
  padding: 20px;
  border-top: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.background};
`;

const InputContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
`;

const TextArea = styled.textarea`
  flex: 1;
  min-height: 80px;
  max-height: 200px;
  padding: 12px 16px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 12px;
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  font-size: 14px;
  font-family: inherit;
  resize: none;
  outline: none;
  transition: all 0.2s ease;

  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }

  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const SendButton = styled(motion.button)`
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 12px;
  background: ${props => props.theme.colors.primary};
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #005a9e;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingDots = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
`;

const Dot = styled(motion.div)`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${props => props.theme.colors.primary};
`;

const ProgressContainer = styled(motion.div)`
  margin: 16px 20px;
  padding: 16px;
  background: ${props => props.theme.colors.primary}10;
  border: 1px solid ${props => props.theme.colors.primary}30;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ProgressHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ProgressIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  font-weight: 600;
`;

const ProgressText = styled.div`
  flex: 1;
`;

const ProgressTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin-bottom: 4px;
`;

const ProgressDescription = styled.div`
  font-size: 12px;
  color: ${props => props.theme.colors.textSecondary};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: ${props => props.theme.colors.border};
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, ${props => props.theme.colors.primary}, #7C3AED);
  border-radius: 3px;
`;

const StageBadges = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StageBadge = styled.div`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${props => props.active ? props.theme.colors.primary : props.theme.colors.border};
  color: ${props => props.active ? 'white' : props.theme.colors.textSecondary};
  transition: all 0.3s ease;
`;

const CheckIcon = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #10B981;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 10px;
`;

function AIAssistant({ mode, content, onResponse, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(-1);
  const [progress, setProgress] = useState(null);
  const [isStopping, setIsStopping] = useState(false);
  const [currentResponse, setCurrentResponse] = useState('');
  const abortControllerRef = useRef(null);
  const textareaRef = useRef(null);
  const chatAreaRef = useRef(null);

  const modeConfig = {
    write: {
      icon: Lightbulb,
      title: '글 작성 도우미',
      description: localStorage.getItem('oneline_model') === 'oneline-custom' 
        ? '주제와 구체적인 요구사항을 입력하면 전문 보고서를 작성해드립니다.'
        : '주제나 요구사항을 입력하면 AI가 글을 작성해드립니다.',
      placeholder: localStorage.getItem('oneline_model') === 'oneline-custom' 
        ? '예: "주제: 디지털 마케팅 전략 보고서\n요구사항: 1) 시장 현황 분석 2) 경쟁사 분석 3) 실행 방안 제시 4) 예산 계획"'
        : '예: "마케팅 전략에 대한 블로그 포스트를 작성해주세요"'
    },
    edit: {
      icon: Edit3,
      title: '글 수정 도우미',
      description: '현재 문서를 개선하거나 특정 부분을 수정해드립니다.',
      placeholder: '예: "더 전문적으로 수정해주세요" 또는 "간단하게 요약해주세요"'
    },
    question: {
      icon: MessageCircle,
      title: '질문 도우미',
      description: '문서나 주제에 대해 질문하고 답변을 받을 수 있습니다.',
      placeholder: '궁금한 점을 자유롭게 질문해보세요'
    }
  };

  const currentMode = modeConfig[mode];

  // 진행 상태 콜백 처리
  const handleProgress = (progressData) => {
    setProgress(progressData);
    
    // 완료된 경우 3초 후 진행 상태 초기화
    if (progressData.status === 'complete') {
      setTimeout(() => {
        setProgress(null);
      }, 3000);
    }
  };

  // 중지 함수
  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsStopping(true);
      
      // 현재까지 작성된 내용을 적용
      if (currentResponse.trim()) {
        onResponse(currentResponse);
        setMessages(prev => [...prev, {
          type: 'assistant',
          content: currentResponse,
          timestamp: new Date().toISOString()
        }]);
      }
      
      setIsLoading(false);
      setProgress(null);
      setCurrentResponse('');
    }
  };

  useEffect(() => {
    // 초기 메시지 설정
    const isConfigured = aiService.isConfigured();
    let initialMessage;
    
    if (mode === 'edit' && content) {
      initialMessage = `현재 문서를 확인했습니다. 어떤 부분을 수정하고 싶으신가요?\n\n현재 문서 내용:\n"${content.substring(0, 200)}${content.length > 200 ? '...' : ''}"`;
    } else {
      initialMessage = `안녕하세요! ${currentMode.title}입니다. ${currentMode.description}`;
    }
    
    if (!isConfigured) {
      initialMessage += `\n\n InOne AI가 응답을 제공합니다. 실제 AI 기능을 사용하려면 상단의 설정 버튼에서 API 키를 입력해주세요.`;
    }
    
    setMessages([{
      id: 1,
      type: 'assistant',
      content: initialMessage
    }]);
  }, [mode, content]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: input.trim()
    };

    setMessages(prev => [...prev, userMessage]);
    const userInput = input.trim();
    setInput('');
    setIsLoading(true);

    // AI 응답 메시지를 미리 생성 (스트리밍용)
    const aiMessageId = Date.now() + 1;
    const assistantMessage = {
      id: aiMessageId,
      type: 'assistant',
      content: '',
      isStreaming: true
    };

    setMessages(prev => [...prev, assistantMessage]);

    // 스크롤을 맨 아래로
    setTimeout(() => {
      if (chatAreaRef.current) {
        chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
      }
    }, 100);

    try {
      // API 키가 설정되어 있으면 실제 API 호출, 아니면 시뮬레이션
      console.log('API 설정 상태:', aiService.isConfigured());
      console.log('API 설정:', aiService.isConfigured() ? '설정됨' : '설정 안됨');
      
      if (aiService.isConfigured()) {
        console.log('스트리밍 API 호출 시작...');
        
        // 스트리밍 콜백 함수
        const onChunk = (chunk) => {
          setCurrentResponse(prev => prev + chunk);
          setMessages(prev => prev.map(msg => 
            msg.id === aiMessageId 
              ? { ...msg, content: msg.content + chunk }
              : msg
          ));
          
          // 스트리밍 중에도 스크롤 유지
          setTimeout(() => {
            if (chatAreaRef.current) {
              chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
            }
          }, 50);
        };

        // 스트리밍 API 호출
        switch (mode) {
          case 'write':
            // WordEditor에 글이 있으면 기존 문서를 바탕으로 작성
            const hasExistingContent = content && content.trim().length > 0;
            let writePrompt = userInput;
            if (hasExistingContent) {
              writePrompt = `기존 문서 내용을 바탕으로 다음 요청사항을 작성해주세요:\n\n${userInput}\n\n주의: 기존 문서의 스타일, 톤, 구조를 유지하면서 추가하거나 보완해주세요.`;
            }
            await aiService.generateContentStream(writePrompt, {}, onChunk, handleProgress);
            break;
          case 'edit':
            await aiService.editContentStream(content, userInput, {}, onChunk);
            break;
          case 'question':
            await aiService.answerQuestionStream(userInput, content, {}, onChunk);
            break;
          default:
            // WordEditor에 글이 있으면 기존 문서를 바탕으로 작성
            const hasExistingContentDefault = content && content.trim().length > 0;
            let writePromptDefault = userInput;
            if (hasExistingContentDefault) {
              writePromptDefault = `기존 문서 내용을 바탕으로 다음 요청사항을 작성해주세요:\n\n${userInput}\n\n주의: 기존 문서의 스타일, 톤, 구조를 유지하면서 추가하거나 보완해주세요.`;
            }
            await aiService.generateContentStream(writePromptDefault, {}, onChunk, handleProgress);
        }
        console.log('스트리밍 API 응답 완료');
        setCurrentResponse(''); // 완료 후 현재 응답 초기화
      } else {
        console.log('시뮬레이션된 응답 사용 (스트리밍 효과)');
        
        // 시뮬레이션 응답을 스트리밍처럼 표시
        // write 모드: 기존 문서가 있으면 바탕으로 작성
        const hasExistingContent = content && content.trim().length > 0;
        const simulatedInput = (mode === 'write' && hasExistingContent)
          ? `기존 문서 내용을 바탕으로 다음 요청사항을 작성해주세요:\n\n${userInput}\n\n주의: 기존 문서의 스타일, 톤, 구조를 유지하면서 추가하거나 보완해주세요.`
          : userInput;
        const simulatedResponse = aiService.getSimulatedResponse(mode, simulatedInput, content);
        
        // 시뮬레이션 응답을 단어별로 나누어 스트리밍 효과 생성
        const words = simulatedResponse.split(' ');
        let currentContent = '';
        
        for (let i = 0; i < words.length; i++) {
          currentContent += (i > 0 ? ' ' : '') + words[i];
          setMessages(prev => prev.map(msg => 
            msg.id === aiMessageId 
              ? { ...msg, content: currentContent }
              : msg
          ));
          
          // 스크롤 유지
          setTimeout(() => {
            if (chatAreaRef.current) {
              chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
            }
          }, 50);
          
          // 각 단어마다 30ms 대기 (자연스러운 타이핑 효과)
          await new Promise(resolve => setTimeout(resolve, 30));
        }
      }

      // 스트리밍 완료 표시
      setMessages(prev => prev.map(msg => 
        msg.id === aiMessageId 
          ? { ...msg, isStreaming: false }
          : msg
      ));
      
      // 응답이 글 작성이나 수정인 경우
      if (mode === 'write' || mode === 'edit') {
        setTimeout(() => {
          const finalContent = messages.find(msg => msg.id === aiMessageId)?.content || '';
          if (finalContent) {
            onResponse(finalContent);
          }
        }, 1000);
      }
    } catch (error) {
      console.error('AI 응답 오류:', error);
      setMessages(prev => prev.map(msg => 
        msg.id === aiMessageId 
          ? { 
              ...msg, 
              content: `오류가 발생했습니다: ${error.message}\n\nAPI 키가 설정되지 않았거나 네트워크 오류일 수 있습니다. 설정에서 API 키를 확인해주세요.`,
              isStreaming: false
            }
          : msg
      ));
    } finally {
      setIsLoading(false);
    }
  };


  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const copyToClipboard = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(-1), 2000);
    } catch (error) {
      console.error('복사 실패:', error);
    }
  };

  return (
    <AnimatePresence>
      <Overlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <AssistantContainer
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
        >
          <AssistantHeader>
            <HeaderInfo>
              <ModeIcon>
                <currentMode.icon size={20} />
              </ModeIcon>
              <ModeInfo>
                <ModeTitle>{currentMode.title}</ModeTitle>
                <ModeDescription>{currentMode.description}</ModeDescription>
              </ModeInfo>
            </HeaderInfo>
            <CloseButton
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
            >
              <X size={18} />
            </CloseButton>
          </AssistantHeader>

          <ChatArea ref={chatAreaRef}>
            {messages.map((message, index) => (
              <Message
                key={message.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <MessageAvatar isUser={message.type === 'user'}>
                  {message.type === 'user' ? <User size={16} /> : <Bot size={16} />}
                </MessageAvatar>
                <MessageContent 
                  isUser={message.type === 'user'} 
                  isStreaming={message.isStreaming}
                >
                  <div style={{ whiteSpace: 'pre-wrap' }}>{message.content}</div>
                  {message.type === 'assistant' && (
                    <MessageActions>
                      <ActionButton
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => copyToClipboard(message.content, index)}
                      >
                        {copiedIndex === index ? <Check size={12} /> : <Copy size={12} />}
                        {copiedIndex === index ? '복사됨' : '복사'}
                      </ActionButton>
                    </MessageActions>
                  )}
                </MessageContent>
              </Message>
            ))}
            
            {/* 진행 표시 (모드에 따라 문구/뱃지 변경) */}
            {progress && (
              <ProgressContainer
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <ProgressHeader>
                  <ProgressIcon>
                    {progress.stage === 'part1' ? '1' : progress.stage === 'part2' ? '2' : ''}
                  </ProgressIcon>
                  <ProgressText>
                    <ProgressTitle>{progress.message}</ProgressTitle>
                    <ProgressDescription>
                      {(() => {
                        const model = (typeof window !== 'undefined' && localStorage.getItem('oneline_model')) || 'oneline-chat';
                        if (progress.stage === 'writing') {
                          return model === 'oneline-reasoner' ? '논문을 작성하고 있습니다...' : '보고서를 작성하고 있습니다...';
                        }
                        if (model === 'oneline-reasoner') {
                          return progress.stage === 'part1'
                            ? '논문의 구조와 배경, 방법론을 작성하고 있습니다...'
                            : '실험 결과와 결론을 작성하고 있습니다...';
                        }
                        return progress.stage === 'part1'
                          ? '보고서의 개요와 배경, 핵심 내용 전반부를 작성하고 있습니다...'
                          : '분석 및 결론을 작성하고 있습니다...';
                      })()}
                    </ProgressDescription>
                  </ProgressText>
                </ProgressHeader>
                
                <ProgressBar>
                  <ProgressFill
                    initial={{ width: 0 }}
                    animate={{ 
                      width: progress.stage === 'part1' ? '50%' : '100%'
                    }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                  />
                </ProgressBar>
                
                <StageBadges>
                  {(() => {
                    const model = (typeof window !== 'undefined' && localStorage.getItem('oneline_model')) || 'oneline-chat';
                    if (progress.stage === 'writing') {
                      return (
                        <StageBadge active>
                          {progress.status === 'complete' && <CheckIcon>✓</CheckIcon>}
                          {model === 'oneline-reasoner' ? '논문 작성' : '보고서 작성'}
                        </StageBadge>
                      );
                    }
                    return (
                      <>
                        <StageBadge active={progress.stage === 'part1' || progress.stage === 'part2'}>
                          {progress.stage === 'part2' && <CheckIcon>✓</CheckIcon>}
                          {(model === 'oneline-reasoner') ? '논문 구조' : '보고서 구조'}
                        </StageBadge>
                        <StageBadge active={progress.stage === 'part2'}>
                          {progress.stage === 'part2' && progress.status === 'complete' && <CheckIcon>✓</CheckIcon>}
                          {(model === 'oneline-reasoner') ? '실험 & 결론' : '분석 & 결론'}
                        </StageBadge>
                      </>
                    );
                  })()}
                </StageBadges>
              </ProgressContainer>
            )}
            
            {isLoading && (
              <Message
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <MessageAvatar>
                  <Bot size={16} />
                </MessageAvatar>
                <MessageContent>
                  <LoadingDots>
                    <Dot
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                    />
                    <Dot
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                    />
                    <Dot
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                    />
                    <span style={{ marginLeft: '8px', fontSize: '12px' }}>생각 중...</span>
                  </LoadingDots>
                </MessageContent>
              </Message>
            )}
          </ChatArea>

          <InputArea>
            <InputContainer>
              <TextArea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={currentMode.placeholder}
                disabled={isLoading}
              />
              {isLoading ? (
                <SendButton
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleStop}
                  style={{ backgroundColor: '#ff4444' }}
                >
                  중지
                </SendButton>
              ) : (
                <SendButton
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleSend}
                  disabled={!input.trim() || isLoading}
                >
                  <Send size={18} />
                </SendButton>
              )}
            </InputContainer>
          </InputArea>
        </AssistantContainer>
      </Overlay>
    </AnimatePresence>
  );
}

export default AIAssistant;
